import React from "react";
import { ChatMessage } from "../../../store/aiChatSlice";
import AiChatEmptyState from "./AiChatEmptyState";
import AiChatMessages from "./AiChatMessages";

//This component handles rendering the body of the AI chat panel, including messages and empty states.

// Props definition for AiChatBody component
interface AiChatBodyProps {
  pinnedPrompts?: string[];
  onPromptSelect?: (prompt: string) => void;
  messages: ChatMessage[];
  isLoading: boolean;
  isFetchingConversation: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  messagesClassName?: string;
  hideMessagesWhenEmpty?: boolean;
  emptyStateVariant?: "panel" | "analyst";
  conversationId?: string;
  scheduleRunnerId?: string;
  shareReturnTo?: { modalView: string; properties: Record<string, unknown> };
  /** Rendered under the empty-state heading (analyst variant) — e.g. the chat input. */
  inputSlot?: React.ReactNode;
  /** Re-send a prompt for a failed AI message (Retry button). */
  onRetry?: (promptText: string) => void;
}
// AiChatBody component definition
const AiChatBody: React.FC<AiChatBodyProps> = ({
  pinnedPrompts,
  onPromptSelect,
  messages,
  isLoading,
  isFetchingConversation,
  messagesEndRef,
  messagesClassName,
  hideMessagesWhenEmpty = false,
  emptyStateVariant = "panel",
  conversationId,
  scheduleRunnerId,
  shareReturnTo,
  inputSlot,
  onRetry,
}) => {
  // Determine if the empty state should be shown
  const showEmptyState = messages.length === 0 && !isFetchingConversation;
  const shouldShowMessages = !hideMessagesWhenEmpty || !showEmptyState;
  const resolvedMessagesClassName =
    messagesClassName ?? "flex-1 space-y-3 overflow-y-auto px-3 pb-6";

  return (
    <>
    {/* Render empty state if there are no messages and not fetching conversation */}
      {showEmptyState && (
        <AiChatEmptyState
          variant={emptyStateVariant}
          pinnedPrompts={pinnedPrompts}
          onPromptSelect={onPromptSelect}
          inputSlot={inputSlot}
        />
      )}
      {/* Render messages if they should be shown */}
      {shouldShowMessages && (
        <div className={resolvedMessagesClassName}>
          <AiChatMessages
            messages={messages}
            isLoading={isLoading}
            isFetchingConversation={isFetchingConversation}
            messagesEndRef={messagesEndRef}
            conversationId={conversationId}
            scheduleRunnerId={scheduleRunnerId}
            shareReturnTo={shareReturnTo}
            onRetry={onRetry}
          />
        </div>
      )}
    </>
  );
};

export default AiChatBody;